type QueuedFunction = (...args: unknown[]) => Promise<unknown>;

export class PromiseQueue {
  private list: [
    QueuedFunction,
    unknown[],
    ((res: unknown) => void) | null,
    ((err: unknown) => void) | null,
  ][] = [];
  private processing: boolean = false;
  private finished: Promise<void> | undefined = undefined;

  private async process() {
    if (this.processing) return;
    this.processing = true;

    let finish: () => void;
    this.finished = new Promise((resolve) => {
      finish = resolve;
    });

    // process queue
    while (this.list.length) {
      const [fn, args, resolve, reject] = this.list.shift()!;
      try {
        const res = await fn(...args);
        if (resolve) resolve(res);
      } catch (err) {
        if (reject) {
          reject(err);
        } else {
          console.error(err);
        }
      }
    }

    this.processing = false;
    finish!();
    this.finished = undefined;
  }

  push<P extends unknown[], R>(
    fn: (...args: P) => Promise<R>,
    ...args: P
  ): this {
    this.list.push([fn as QueuedFunction, args, null, null]);
    this.process();
    return this;
  }
  wait<P extends unknown[], R>(
    fn: (...args: P) => Promise<R>,
    ...args: P
  ): Promise<R> {
    return new Promise((resolve, reject) => {
      this.list.push([
        fn as QueuedFunction,
        args,
        resolve as (res: unknown) => void,
        reject,
      ]);
      this.process();
    });
  }
  proxy<P extends unknown[], R>(
    fn: (...args: P) => Promise<R>
  ): (...args: P) => Promise<R> {
    return (...args: P) => this.wait<P, R>(fn, ...args);
  }
  running() {
    return this.processing;
  }
  remaining() {
    return this.list.length;
  }
  waitForRemaining() {
    return this.finished ?? Promise.resolve();
  }
}
